import * as Sentry from '@sentry/react';

/**
 * Sentry Error Tracking
 *
 * Setup:
 * 1. Create account at https://sentry.io (free tier: 5k errors/month)
 * 2. Create a new React project
 * 3. Copy the DSN and add to VITE_SENTRY_DSN in your .env
 *
 * Features:
 * - Automatic error capture with full stack traces
 * - User context (who experienced the error)
 * - Session replay for debugging
 * - Performance monitoring
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('Sentry: VITE_SENTRY_DSN not set, error tracking disabled');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // Only send errors in production
    enabled: import.meta.env.PROD,
    // Sample rate for performance monitoring (0.1 = 10% of transactions)
    tracesSampleRate: 0.1,
    // Session replay for debugging (captures user actions leading to error)
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
  });
}

/**
 * Set user context for error reports
 * Call this after login
 */
export function setSentryUser(user: {
  id: string;
  email: string;
  restaurantId?: string;
  restaurantName?: string;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });
  if (user.restaurantId) {
    Sentry.setTag('restaurant_id', user.restaurantId);
    Sentry.setTag('restaurant_name', user.restaurantName || 'Unknown');
  }
}

/**
 * Clear user context on logout
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Manually capture an error with context
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Track a custom event (e.g., "payment_failed", "order_voided")
 */
export function trackEvent(name: string, data?: Record<string, unknown>) {
  Sentry.captureMessage(name, {
    level: 'info',
    extra: data,
  });
}

// Re-export ErrorBoundary for wrapping components
export { ErrorBoundary } from '@sentry/react';
